import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  type ReactNode,
} from 'react';
import { useLoaderData } from 'react-router-dom';

import { PAGE_SIZE } from 'src/core/domain/constants';
import { useAuthStatePageReload } from 'src/hooks/useAuthPageReload/useAuthPageReload';
import type {
  ThreadCard,
  FeaturedThreadsResult,
} from 'src/mapper/threadMapper';
import type { CommunityHomeLoaderData } from 'src/pages/CommunityHome';

interface ThreadsState {
  displayThreads: ThreadCard[];
  totalPage: number;
}

type ThreadsAction = {
  type: 'UPDATE_THREADS';
  payload: { threads: ThreadCard[] };
};

const ThreadsContext = createContext<{
  threadsState: ThreadsState;
  threadsDispatch: React.Dispatch<ThreadsAction>;
} | null>(null);

const threadsReducer = (
  threadsState: ThreadsState,
  action: ThreadsAction,
): ThreadsState => {
  switch (action.type) {
    case 'UPDATE_THREADS':
      return {
        ...threadsState,
        displayThreads: action.payload.threads,
      };
    default:
      return threadsState;
  }
};

interface ThreadsProviderProps {
  children: ReactNode;
  initialData: FeaturedThreadsResult;
}

const useRefreshThreads = (
  threadsDispatch: React.Dispatch<ThreadsAction>,
  newThreads: FeaturedThreadsResult,
) => {
  useEffect(() => {
    threadsDispatch({
      type: 'UPDATE_THREADS',
      payload: { threads: newThreads.threads },
    });
  }, [newThreads, threadsDispatch]);
};

export const ThreadsProvider = ({
  children,
  initialData,
}: ThreadsProviderProps) => {
  const { init, isAuthenticated } = useLoaderData() as CommunityHomeLoaderData;
  const [threadsState, threadsDispatch] = useReducer(threadsReducer, {
    displayThreads: initialData.threads,
    totalPage: Math.ceil(initialData.totalThread / PAGE_SIZE),
  });

  useAuthStatePageReload(init, isAuthenticated);
  useRefreshThreads(threadsDispatch, initialData);

  return (
    <ThreadsContext.Provider
      value={{
        threadsState,
        threadsDispatch,
      }}
    >
      {children}
    </ThreadsContext.Provider>
  );
};

export const useThreads = () => {
  const context = useContext(ThreadsContext);
  if (!context) {
    throw new Error('useThreads must be used within a ThreadsProvider');
  }
  return context;
};
